<template>
  <Teleport to="body">
    <Transition name="fade">
      <div v-if="modelValue" class="switching-overlay">
        <div class="switching-overlay__content">
          <v-avatar
            v-if="organizationImage"
            :color="avatarColor"
            size="80"
            rounded="lg"
            class="switching-overlay__avatar"
          >
            <v-img :src="organizationImage" cover />
          </v-avatar>
          <v-avatar
            v-else-if="organization"
            :color="avatarColor"
            size="80"
            rounded="lg"
            class="switching-overlay__avatar"
          >
            <span class="text-h4 font-weight-bold text-white">
              {{ getInitials(organization.name) }}
            </span>
          </v-avatar>

          <v-progress-circular
            indeterminate
            size="32"
            width="3"
            color="primary"
            class="switching-overlay__spinner"
          />

          <p class="switching-overlay__text">
            {{ $t('organizations.switching.loading') }}
          </p>

          <p v-if="organization" class="switching-overlay__org-name">
            {{ organization.name }}
          </p>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { getInitials, getColorFromUid } from '../../core/utils'
import type { SwitchingOrganization } from '../composables/useOrganizationSwitcher'

const props = defineProps<{
  modelValue: boolean
  organization?: SwitchingOrganization
}>()

const avatarColor = computed(() =>
  props.organization ? getColorFromUid(props.organization.uid) : 'primary'
)

const organizationImage = computed(() =>
  props.organization?.icon_url || null
)
</script>

<style scoped lang="scss">
.switching-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(var(--v-theme-surface), 0.95);
  backdrop-filter: blur(8px);

  &__content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    text-align: center;
  }

  &__avatar {
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  }

  &__spinner {
    margin-top: 8px;
  }

  &__text {
    font-size: 0.875rem;
    color: rgba(var(--v-theme-on-surface), 0.6);
    margin: 0;
  }

  &__org-name {
    font-size: 1.25rem;
    font-weight: 600;
    color: rgb(var(--v-theme-on-surface));
    margin: 0;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
